/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import styled, { css } from 'styled-components';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import {
  getSmallestUrlForWidth,
  resourceList,
  ResourcePropTypes,
} from '@web-stories-wp/media';
import { Icons, Text, THEME_CONSTANTS } from '@web-stories-wp/design-system';
/**
 * Internal dependencies
 */
import useAverageColor from '../../../../../elements/media/useAverageColor';
import LibraryMoveable from '../../shared/libraryMoveable';
import { useDropTargets } from '../../../../dropTargets';
import { useLayout } from '../../../../../app/layout';
import { ContentType } from '../../../../../app/media';
import { useInsertLibraryMediaElement } from '@src/hooks';
import { savedResourcesDimensions } from '../../../../../../../../utils/checkMediaDimensions';
import { getDefaultInsertedElementSize } from '../../../../../utils/getDefaultInsertedElementSize';

const styledTiles = css`
  width: 100%;
  cursor: pointer;
  transition: 0.2s transform, 0.15s opacity;
  border-radius: 4px;
  opacity: 0;
`;

const Image = styled.img`
  ${styledTiles}
`;

// Display the newly uploaded videos without a delay: showWithoutDelay
const Video = styled.video`
  ${styledTiles}
  object-fit: cover;
  position: relative;
  z-index: 1;
  ${({ showWithoutDelay }) => (showWithoutDelay ? 'opacity: 1;' : '')}
`;

const DurationWrapper = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: ${({ theme }) => theme.colors.opacity.black64};
  border-radius: 100px;
  height: 18px;
  padding: 0 6px;
  z-index: 2;
  pointer-events: none;
`;
const MuteWrapper = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  height: 24px;
  width: 24px;
  background: ${({ theme }) => theme.colors.opacity.black64};
  border-radius: 100px;
  padding: 4px;
  z-index: 2;
  pointer-events: none;
`;
const Duration = styled(Text).attrs({
  forwardedAs: 'span',
  size: THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.X_SMALL,
})`
  color: ${({ theme }) => theme.colors.fg.primary};
  display: block;
`;

const PosterImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  object-fit: cover;
  pointer-events: none;
  z-index: 0;
`;

const CloneImg = styled.img`
  opacity: 0;
  width: ${({ width }) => `${width}px`};
  height: ${({ height }) => `${height}px`};
  position: absolute;
`;

function InnerElement({
  type,
  src,
  resource,
  alt,
  width,
  height,
  onClick,
  onDimensionsUpdate,
  showVideoDetail,
  mediaElement,
  active,
  noAudio = false,
  isMuted,
  videoProps: baseVideoProps,
  isThumbnailBatchReady,
  onThumbnailReady,
}) {
  const pageSize = useLayout(({ state: { pageWidth, pageHeight } }) => ({
    width: pageWidth,
    height: pageHeight,
  }));

  const newVideoPosterRef = useRef(null);
  const hiddenPoster = useRef(null);
  const hasReportedThumbnailRef = useRef(false);
  // Firefox deprioritizes poster image loads when many video sources attach
  // at once, so wait for the thumbnail batch to finish before enabling sources.
  const [allowVideoSource, setAllowVideoSource] = useState(
    Boolean(isThumbnailBatchReady)
  );
  const mediaBaseColor = useRef(null);
  const { startDownloadingResource } = useInsertLibraryMediaElement();

  const {
    state: { draggingResource },
    actions: { handleDrag, handleDrop, setDraggingResource },
  } = useDropTargets();

  // Get the base color of the media for using when adding a new image,
  // needed for example when droptargeting to bg.
  const setAverageColor = (color) => {
    mediaBaseColor.current = color;
  };

  const isVideoType = [ContentType.VIDEO, ContentType.GIF].includes(type);

  useAverageColor(isVideoType ? hiddenPoster : mediaElement, setAverageColor);

  useEffect(() => {
    hasReportedThumbnailRef.current = false;
    setAllowVideoSource(false);
  }, [resource.id]);

  useEffect(() => {
    if (!allowVideoSource && isThumbnailBatchReady) {
      setAllowVideoSource(true);
    }
  }, [allowVideoSource, isThumbnailBatchReady]);

  useEffect(() => {
    // assign display poster for videos
    if (resource.poster && resource.poster.includes('blob')) {
      newVideoPosterRef.current = resource.poster;
    }
  }, [resource.poster]);

  const handleInsert = useCallback(
    (insertResource = resource) => {
      handleDrop({
        ...insertResource,
        baseColor: mediaBaseColor.current,
      });
    },
    [handleDrop, resource]
  );

  const reportThumbnailReady = useCallback(() => {
    if (hasReportedThumbnailRef.current) {
      return;
    }
    hasReportedThumbnailRef.current = true;
    onThumbnailReady?.(resource.id);
  }, [onThumbnailReady, resource.id]);

  const setVideoOpacity = useCallback((event) => {
    const target = event?.currentTarget;
    if (!target || target.style.opacity === '1') {
      return;
    }
    target.style.opacity = 1;
  }, []);

  const onImageLoad = (event) => {
    if (mediaElement.current && !isVideoType) {
      mediaElement.current.style.opacity = 1;
    }

    if (resource.width > 0 && resource.height > 0) {
      reportThumbnailReady();
      return;
    }

    const imageWidth = event.target.naturalWidth;
    const imageHeight = event.target.naturalHeight;

    savedResourcesDimensions.set(resource.id, {
      width: imageWidth,
      height: imageHeight,
      isVideoPoster: resource.type === ContentType.VIDEO,
    });

    onDimensionsUpdate?.(resource.id, imageWidth, imageHeight);
    reportThumbnailReady();
  };
  const onImageError = () => {
    reportThumbnailReady();
  };

  let media;
  const thumbnailURL = resource.galleryOnly
    ? resource.poster
    : getSmallestUrlForWidth(width, resource);
  const { lengthFormatted, poster, mimeType } = resource;
  const displayPoster = poster ?? newVideoPosterRef.current;
  const shouldRenderVideoSource = Boolean(
    allowVideoSource ||
      !displayPoster ||
      active ||
      baseVideoProps?.src ||
      baseVideoProps?.autoPlay
  );
  const shouldUseVideoPoster = shouldRenderVideoSource;

  useEffect(() => {
    if (isVideoType && !displayPoster) {
      reportThumbnailReady();
    }
  }, [displayPoster, isVideoType, reportThumbnailReady]);

  const cloneSize = getDefaultInsertedElementSize({
    width: resource.width || width,
    height: resource.height || height,
    pageWidth: pageSize.width,
    pageHeight: pageSize.height,
  });

  const commonProps = {
    width,
    height,
    alt: alt,
    crossOrigin: 'anonymous',
  };

  const commonImageProps = {
    ...commonProps,
    onLoad: onImageLoad,
    onError: onImageError,
    loading: 'lazy',
    draggable: false,
  };

  const cloneProps = {
    ...commonImageProps,
    width: cloneSize.width,
    height: cloneSize.height,
    onLoad: undefined,
    onError: undefined,
  };

  const imageProps = {
    ...commonImageProps,
    src: thumbnailURL,
    'aria-label': alt,
  };
  const videoProps = {
    ...commonProps,
    'aria-label': alt,
    loop: type === ContentType.GIF,
    muted: true,
    preload: 'metadata',
    showWithoutDelay: !poster || Boolean(newVideoPosterRef.current),
    ...baseVideoProps,
    poster: shouldUseVideoPoster
      ? baseVideoProps?.poster ?? displayPoster
      : undefined,
  };

  if (type === ContentType.IMAGE) {
    media = <Image key={src} {...imageProps} ref={mediaElement} />;
    cloneProps.src = thumbnailURL;
  } else if ([ContentType.VIDEO, ContentType.GIF].includes(type)) {
    media = (
      <>
        {displayPoster && (
          <PosterImage
            ref={hiddenPoster}
            src={displayPoster}
            {...commonImageProps}
            alt=""
            aria-hidden="true"
            loading="eager"
          />
        )}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- No captions/tracks because video is muted. */}
        <Video
          key={src}
          {...videoProps}
          ref={mediaElement}
          onLoadedMetadata={(event) => {
            setVideoOpacity(event);
            if (
              (resource.width > 0 &&
                resource.height > 0 &&
                !savedResourcesDimensions.get(resource.id)?.isVideoPoster) ||
              resource.type !== ContentType.VIDEO
            ) {
              baseVideoProps?.onLoadedMetadata?.(event);
              return;
            }

            const videoWidth = event.target.videoWidth;
            const videoHeight = event.target.videoHeight;

            onDimensionsUpdate?.(resource.id, videoWidth, videoHeight);
            savedResourcesDimensions.set(resource.id, {
              width: videoWidth,
              height: videoHeight,
              isVideoPoster: false,
            });
            baseVideoProps?.onLoadedMetadata?.(event);
          }}
          onLoadedData={(event) => {
            setVideoOpacity(event);
            baseVideoProps?.onLoadedData?.(event);
          }}
          onPlay={(event) => {
            setVideoOpacity(event);
            baseVideoProps?.onPlay?.(event);
          }}
        >
          {shouldRenderVideoSource &&
            (type === ContentType.GIF ? (
              resource.output.src && (
                <source
                  src={resource.output.src}
                  type={resource.output.mimeType}
                />
              )
            ) : (
              <source
                src={getSmallestUrlForWidth(width, resource)}
                type={mimeType}
              />
            ))}
        </Video>
        {type === ContentType.VIDEO &&
          showVideoDetail &&
          lengthFormatted &&
          lengthFormatted !== 'NaN:NaN' && (
            <DurationWrapper>
              <Duration>{lengthFormatted}</Duration>
            </DurationWrapper>
          )}
        {type === ContentType.VIDEO &&
          showVideoDetail &&
          isMuted &&
          !noAudio && (
            <MuteWrapper>
              <Icons.Muted />
            </MuteWrapper>
          )}
      </>
    );
    cloneProps.src = poster;
  }
  if (!media) {
    throw new Error('Invalid media element type.');
  }

  const dragHandler = (event) => {
    if (
      [ContentType.VIDEO, ContentType.GIF].includes(type) &&
      !mediaElement.current?.paused
    ) {
      mediaElement.current?.pause();
    }
    if (!draggingResource) {
      // Drop-targets handling.
      resourceList.set(resource.id, {
        url: thumbnailURL,
        type: 'cached',
      });

      setDraggingResource(resource);
    }
    handleDrag(resource, event.clientX, event.clientY);
  };

  return (
    <>
      {media}
      <LibraryMoveable
        active={active}
        handleDrag={dragHandler}
        handleDragEnd={async () => {
          const downloadedResource = await startDownloadingResource(resource);
          if (downloadedResource) {
            handleInsert(downloadedResource);
          }
        }}
        handleInsert={resource.galleryOnly ? undefined : handleInsert}
        type={resource.type}
        elementProps={{
          resource: {
            ...resource,
            baseColor: mediaBaseColor.current,
          },
        }}
        onClick={onClick(
          type === ContentType.IMAGE ? thumbnailURL : poster,
          mediaBaseColor.current
        )}
        cloneElement={CloneImg}
        cloneProps={cloneProps}
      />
    </>
  );
}

InnerElement.propTypes = {
  type: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  resource: ResourcePropTypes.imageResource,
  alt: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  isMuted: PropTypes.bool,
  noAudio: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  showVideoDetail: PropTypes.bool,
  mediaElement: PropTypes.object,
  active: PropTypes.bool.isRequired,
  isThumbnailBatchReady: PropTypes.bool,
  onThumbnailReady: PropTypes.func,
};

export default InnerElement;
